// Authenticated shell route — Wave 5a Agent 5a.2.
//
// Replaces the W4.1 stub with the real `<AppShell>` (sidebar + content stack
// + banners + global primitives). The route file owns route metadata only;
// the component body lives in `features/shell` so it can be unit-tested
// without a router fixture.
//
// Wave D4: route-level trial hard-block via `beforeLoad`. Probes
// `/api/v1/billing/subscription` on cold-load and redirects expired /
// past-due users to `/billing-locked` BEFORE any authed page renders. The
// component-level `<HardBlockRedirect />` (mounted by `<AppShell>`) stays
// as a fallback for runtime state changes (WS-driven `subscription.updated`
// events that flip the user mid-session).

import { createRoute, redirect } from '@tanstack/react-router';

import { AppShell } from '@/features/shell/AppShell';

import { Route as RootRoute } from '../__root';

interface SubscriptionProbeResponse {
  subscription: {
    status?: string;
    trialEndsAt?: number | null;
    currentPeriodEnd?: number | null;
  } | null;
  trial?: {
    accessTier?: 'full' | 'soft_block' | 'hard_block';
    expired?: boolean;
  };
}

const MS_PER_DAY = 24 * 60 * 60 * 1000;
const SOFT_BLOCK_DAYS_BEFORE_HARD = 30;

/**
 * Server-side derivation may already populate `trial.accessTier`. When it
 * does, trust it; otherwise fall back to a local heuristic that mirrors
 * `TrialStateMachine.deriveTrialState` — keeps the guard correct against
 * older API responses that don't include the envelope.
 */
function shouldHardBlock(data: SubscriptionProbeResponse | null, now: number): boolean {
  if (!data?.subscription) return false;
  const tier = data.trial?.accessTier;
  if (tier === 'hard_block') return true;
  if (tier === 'soft_block' || tier === 'full') return false;

  const sub = data.subscription;
  const status = sub.status;
  const isTrial = status === 'trialing';
  const expired =
    status === 'past_due' ||
    status === 'unpaid' ||
    status === 'canceled' ||
    (isTrial && typeof sub.trialEndsAt === 'number' ? sub.trialEndsAt < now : false);
  if (!expired) return false;
  const enteredAt = Number(sub.currentPeriodEnd ?? sub.trialEndsAt ?? now);
  const elapsedDays = (now - enteredAt) / MS_PER_DAY;
  return elapsedDays >= SOFT_BLOCK_DAYS_BEFORE_HARD;
}

export const Route = createRoute({
  getParentRoute: () => RootRoute,
  id: '_authed',
  beforeLoad: async ({ context, location }) => {
    // Already on the splash — never re-redirect (would loop). The splash
    // itself lives under `/_authed/billing-locked` so logout still works.
    if (location.pathname.startsWith('/billing-locked')) return;
    // Settings → billing remains reachable so the user can resolve the
    // hard-block. GDPR export route too. Auth routes never hit this layout.
    if (
      location.pathname.startsWith('/settings/billing') ||
      location.pathname.startsWith('/api/v1/me/export')
    ) {
      return;
    }
    let data: SubscriptionProbeResponse | null = null;
    try {
      const response = await context.authFetch('/api/v1/billing/subscription', { method: 'GET' });
      if (!response.ok) return;
      data = (await response.json()) as SubscriptionProbeResponse;
    } catch {
      // Probe failures must NOT lock the user out — fall through and let
      // the component-level guard handle it once the cache populates.
      return;
    }
    if (shouldHardBlock(data, Date.now())) {
      throw redirect({ to: '/billing-locked' });
    }
  },
  component: AuthedLayout,
});

function AuthedLayout() {
  // `useAuthGuard()` is invoked inside the AppShell tree (it needs the i18n
  // + toast surface). The shell renders the `<Outlet>` itself.
  return <AppShell />;
}
